import React, { FC, useEffect, useState } from 'react'
import styles from './Sticky.module.scss'
import { Button } from 'components/Phantom/Button'
import cx from 'classnames'
import { useScrollThreshold } from 'components/_hooks/useScrollThreshold'
import { scrollLocked } from 'utils/jss'
import { PriceManager } from 'prices'
import { Icon } from 'components/Phantom/Icon'
import { useScrollDirection } from 'components/_hooks/useScrollDirection'
import { FinancingProvider } from 'components/_utils/utilityTypes'
import { Badge } from 'components/Badge'
import { usePod4Prices, useTotalProductDiscount, useCurrency, useFinancingProvider } from 'stores/prices'
import { useStore } from 'stores/StoreProvider'
import { useIsUS } from 'stores/settings'
export interface StickyProps {
	stickyName: string
	price: string
	strikePrice: string
	monthlyPrice: string
	months: number
	showFinancing: boolean
	discount: string
	discountLine?: string
	discountLineMobile?: string
	setHeight: (height: number) => void
	href?: string
	ctaText?: string
	hideBenefits?: boolean
	scrollThreshold?: number
}

export const StickyConnected: FC<Partial<StickyProps>> = (props) => {
	const settings = useStore((state) => state.settings.settings)
	const setCoverStickyHeight = useStore((state) => state.settings.setCoverStickyHeight)

	const currency = useCurrency()
	const pod4Prices = usePod4Prices()
	const totalDiscount = useTotalProductDiscount()

	return (
		<Sticky
			stickyName={props.stickyName ?? 'Pod 4 Ultra'}
			price={pod4Prices.priceFormatted}
			strikePrice={pod4Prices.comparePriceFormatted}
			monthlyPrice={pod4Prices.monthlyPriceFormatted}
			months={pod4Prices.maxMonthsAtZeroInterest}
			showFinancing={PriceManager.currencyHasAffirm(currency)}
			discount={totalDiscount.formatted + ' off'}
			discountLine={settings.stickyCardTag}
			discountLineMobile={settings.stickyCardTagMobile}
			setHeight={setCoverStickyHeight}
			{...props}
		/>
	)
}

const Sticky: FC<StickyProps> = (props) => {
	const { stickyName = 'Pod 4 Ultra', ctaText = 'Shop now', hideBenefits = false } = props
	const [stickyVisible, setStickyVisible] = useState(false)
	const headerWiped = useScrollThreshold(props.scrollThreshold)
	const scrollDirection = useScrollDirection(100)

	const financingProvider = useFinancingProvider()
	const isUS = useIsUS()

	useEffect(() => {
		const header = document.querySelector('header')
		const isInApp = window.location.href.includes('utm_source=app')

		const shouldSwipeHeader = headerWiped && scrollDirection !== 'up'
		const resizeListener = () => {
			if (header && shouldSwipeHeader) {
				document.body.style.setProperty('--nav-translate', `-100%`)
			} else if (!scrollLocked) {
				document.body.style.setProperty('--nav-translate', `0px`)
			}
		}

		if (!isInApp) {
			setStickyVisible(shouldSwipeHeader)
			resizeListener()
		}

		window.addEventListener('resize', resizeListener)
		return () => {
			window.removeEventListener('resize', resizeListener)
		}
	}, [headerWiped, scrollDirection])

	useEffect(() => {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setStickyVisible(false)
				}
			})
		})

		const footer = document.getElementsByTagName('footer')
		for (const el of footer) {
			observer.observe(el)
		}

		return () => {
			observer.disconnect()
		}
	})

	useEffect(() => {
		if (stickyVisible) {
			document.body.style.setProperty('--shortcut_bump', '6.5rem')
		} else {
			document.body.style.setProperty('--shortcut_bump', '0px')
		}
	}, [stickyVisible])

	const stickyDiscountTag = (
		<div>
			<Badge.Orange type={'discount'}>{props.discount}</Badge.Orange>
		</div>
	)

	return (
		<div
			className={cx(styles.container, { [styles.container_visible]: stickyVisible })}
			id={'sticky-bar'}
			style={{
				visibility: stickyVisible ? 'visible' : 'hidden',
			}}
		>
			<div className={styles.inner}>
				<div>
					<div className={styles.sticky_title_and_discount}>
						<h2 className={styles.sticky_title_desktop}>{stickyName} </h2>
						{stickyDiscountTag}
					</div>
					<StickyBenefits
						financingProvider={financingProvider}
						hideBenefits={hideBenefits}
					/>
				</div>

				<>
					<Button.Dark
						key={`sticky-shop-now-mobile-${stickyName}-${stickyVisible}`}
						id={'sticky-shop-now-mobile'}
						href={props.href || '/product/pod-cover/'}
						className={styles.mobile_button}
					>
						{ctaText}
					</Button.Dark>
					<Button.Dark
						key={`sticky-shop-now-desktop-${stickyName}-${stickyVisible}`}
						id={'sticky-shop-now-desktop'}
						href={props.href || '/product/pod-cover/'}
						className={styles.desktop_button}
					>
						{ctaText}
					</Button.Dark>
				</>
			</div>
		</div>
	)
}

const StickyBenefits = ({ financingProvider, hideBenefits }: { financingProvider: FinancingProvider; hideBenefits: boolean }) => {
	if (hideBenefits) return null
	return (
		<div className={styles.financing_content}>
			{financingProvider !== 'none' && (
				<p className={styles.financing_item}>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="17"
						viewBox="0 0 16 17"
						fill="none"
					>
						<path
							d="M13.9967 5.1C13.9856 4.98796 13.9463 4.88057 13.8824 4.78788C13.8185 4.69518 13.7321 4.62022 13.6313 4.57L8.298 1.90333C8.20538 1.85699 8.10324 1.83286 7.99967 1.83286C7.8961 1.83286 7.79396 1.85699 7.70134 1.90333L2.368 4.57C2.26752 4.6205 2.18141 4.69556 2.11766 4.78821C2.05392 4.88086 2.01459 4.9881 2.00334 5.1C1.996 5.17133 1.36267 12.278 7.72934 15.1093C7.81441 15.1477 7.90667 15.1676 8 15.1676C8.09333 15.1676 8.1856 15.1477 8.27067 15.1093C14.6373 12.278 14.004 5.172 13.9967 5.1ZM8 13.7647C3.48734 11.5833 3.274 6.928 3.31067 5.59L8 3.24533L12.686 5.58867C12.7107 6.91467 12.4673 11.6007 8 13.7647Z"
							fill="#999999"
						/>
						<path
							d="M7.33335 8.8907L5.80469 7.36203L4.86202 8.3047L7.33335 10.776L11.138 6.97136L10.1954 6.0287L7.33335 8.8907Z"
							fill="#999999"
						/>
					</svg>
					<span>
						Financing through <span>{financingProvider}</span>
					</span>
				</p>
			)}
			<p>
				<Icon
					name={'MoonLight'}
					color={'#999999'}
				/>
				<span>Try risk-free for 30 nights</span>
			</p>
		</div>
	)
}
